/**
 * Base API client with fetch, error handling, and toast integration.
 */
import { toastActions } from '../state/toast.state';
import { authActions } from '../state/auth.state';
import { environment } from '../environments/environment';
import type { ProblemDetails } from './types';

export interface ApiError {
  status: number;
  title: string;
  traceId: string;
  errors?: Record<string, string[]>;
}

export interface ApiResponse<T> {
  data?: T;
  error?: ApiError;
  supportId?: string;
}

function getConfiguredApiBaseUrl(): string | null {
  const baseUrl = environment.apiBaseUrl?.trim();
  if (!baseUrl) {
    return null;
  }

  return baseUrl.replace(/\/+$/, '');
}

export function buildApiUrl(url: string): string {
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  const apiPath = `/api${normalizedPath}`;
  const baseUrl = getConfiguredApiBaseUrl();

  return baseUrl ? `${baseUrl}${apiPath}` : apiPath;
}

/**
 * Make an API request with automatic error handling and toast notifications.
 *
 * @param url - API endpoint path (without /api prefix)
 * @param options - Fetch options
 * @returns Response with data, error, and supportId
 */
export async function apiRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const headers = new Headers(options.headers);

  // Set Content-Type for requests with body
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Add Authorization header with Bearer token if available
  if (!headers.has('Authorization')) {
    const token = await authActions.getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  try {
    const response = await fetch(buildApiUrl(url), { ...options, headers });
    const supportId = response.headers.get('x-support-id') ?? undefined;

    if (!response.ok) {
      let error: ApiError;

      try {
        const problemDetails: ProblemDetails = await response.json();
        error = {
          status: response.status,
          title: problemDetails.title ?? 'An unexpected error occurred',
          traceId: problemDetails.traceId ?? '',
          errors: problemDetails.errors,
        };
      } catch {
        // Response wasn't JSON
        error = {
          status: response.status,
          title: `HTTP ${response.status}: ${response.statusText}`,
          traceId: '',
        };
      }

      // Show toast for non-validation errors
      // Validation errors (400 with errors map) should be handled by the form
      if (response.status !== 400 || !error.errors) {
        toastActions.error(error.title, supportId);
      }

      return { error, supportId };
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { data: undefined, supportId };
    }

    const data = await response.json();
    return { data, supportId };
  } catch (e) {
    // Network error or fetch failed
    toastActions.error('Network error. Please check your connection.');
    return {
      error: {
        status: 0,
        title: 'Network error',
        traceId: '',
      },
    };
  }
}

/**
 * GET request helper.
 */
export function apiGet<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { ...options, method: 'GET' });
}

/**
 * POST request helper.
 */
export function apiPost<T>(
  url: string,
  body?: unknown,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PUT request helper.
 */
export function apiPut<T>(
  url: string,
  body?: unknown,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PATCH request helper.
 */
export function apiPatch<T>(
  url: string,
  body?: unknown,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, {
    ...options,
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * DELETE request helper.
 */
export function apiDelete<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { ...options, method: 'DELETE' });
}
